import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type LangCode = "en" | "hi";

type Translations = Record<string, Record<LangCode, string>>;

const translations: Translations = {
  // Header
  "nav.howItWorks": { en: "How It Works", hi: "यह कैसे काम करता है" },
  "nav.getStarted": { en: "Get Started", hi: "शुरू करें" },
  "nav.login": { en: "Login", hi: "लॉगिन" },
  "nav.signUp": { en: "Sign Up", hi: "साइन अप करें" },
  "nav.language": { en: "Language", hi: "भाषा" },

  // Send Enquiry Page
  "inquiry.title": { en: "Send Enquiry", hi: "पूछताछ भेजें" },
  "inquiry.subtitle": { en: "Reach out to {name} — share your goals and preferred availability.", hi: "{name} से संपर्क करें — अपने लक्ष्य और उपलब्धता साझा करें।" },
  "inquiry.message": { en: "Your message", hi: "आपका संदेश" },
  "inquiry.messagePlaceholder": { en: "Hello {name},\nI am looking for a teacher for...\nI would like to start lessons as soon as possible.", hi: "नमस्ते {name},\nमैं एक शिक्षक की तलाश में हूँ...\nमैं जल्द से जल्द कक्षाएं शुरू करना चाहता/चाहती हूँ।" },
  "inquiry.contact": { en: "Contact information", hi: "संपर्क जानकारी" },
  "inquiry.contactNote": { en: "Only shared with teachers you select.", hi: "केवल आपके चयनित शिक्षकों के साथ साझा किया जाएगा।" },
  "inquiry.email": { en: "Email", hi: "ईमेल" },
  "inquiry.phone": { en: "Phone (optional)", hi: "फ़ोन (वैकल्पिक)" },
  "inquiry.preferredTime": { en: "Preferred day & time", hi: "पसंदीदा दिन और समय" },
  "inquiry.timeNote": { en: "Pick up to 5 time slots that work for you. The teacher will confirm one.", hi: "अपने लिए 5 समय स्लॉट चुनें। शिक्षक एक की पुष्टि करेंगे।" },
  "inquiry.selectDate": { en: "Select a date", hi: "तारीख चुनें" },
  "inquiry.pickTime": { en: "Pick a time on", hi: "समय चुनें" },
  "inquiry.selected": { en: "Selected", hi: "चयनित" },
  "inquiry.slotsSelected": { en: "Your preferred times will be shared", hi: "आपके पसंदीदा समय साझा किए जाएंगे" },
  "inquiry.send": { en: "Send Enquiry", hi: "पूछताछ भेजें" },
  "inquiry.sending": { en: "Sending...", hi: "भेज रहे हैं..." },
  "inquiry.successTitle": { en: "✅ Inquiry Sent Successfully!", hi: "✅ पूछताछ सफलतापूर्वक भेजी गई!" },
  "inquiry.successDesc": { en: "Your inquiry has been sent to {name}. They typically respond {time}. You can track it from your dashboard.", hi: "{name} को आपकी पूछताछ भेज दी गई है। वे आमतौर पर {time} में जवाब देते हैं।" },
  "inquiry.responds": { en: "Responds", hi: "जवाब देते हैं" },
  "inquiry.back": { en: "Back", hi: "वापस" },

  // Home / Hero
  "home.title": { en: "Home", hi: "होम" },

  // Hero Section
  "hero.badge": { en: "Rated 4.8/5 by 10,000+ students", hi: "10,000+ छात्रों द्वारा 4.8/5 रेटेड" },
  "hero.headline1": { en: "Find Your Perfect", hi: "अपना परफेक्ट" },
  "hero.headline2": { en: "Teacher", hi: "शिक्षक" },
  "hero.tagline": { en: "Find. Connect. Learn. Succeed.", hi: "खोजें। जुड़ें। सीखें। सफल हों।" },
  "hero.subheadline": { en: "Expert, verified teachers for every subject and level — online or near you.", hi: "हर विषय और स्तर के लिए विशेषज्ञ, सत्यापित शिक्षक — ऑनलाइन या आपके पास।" },
  "hero.format.oneOnOne": { en: "1-on-1 Sessions", hi: "1-on-1 सेशन" },
  "hero.format.group": { en: "Group Classes", hi: "ग्रुप क्लासेस" },
  "hero.format.crash": { en: "Crash Courses", hi: "क्रैश कोर्सेस" },
  "hero.tab.oneOnOne": { en: "1-on-1 Teacher", hi: "1-on-1 शिक्षक" },
  "hero.tab.group": { en: "Group Classes", hi: "ग्रुप क्लासेस" },
  "hero.search.coach": { en: "Search for a subject, skill or teacher...", hi: "विषय, कौशल या शिक्षक खोजें..." },
  "hero.search.group": { en: "Search group classes...", hi: "ग्रुप क्लासेस खोजें..." },
  "hero.search.btn": { en: "Search", hi: "खोजें" },
  "hero.browse": { en: "Browse:", hi: "ब्राउज़ करें:" },
  "hero.rating": { en: "from 10,000+ students", hi: "10,000+ छात्रों द्वारा" },
  "hero.verified": { en: "All teachers verified", hi: "सभी शिक्षक सत्यापित" },
  "hero.trial": { en: "Free trial session", hi: "मुफ़्त ट्रायल सेशन" },
  "hero.live": { en: "live sessions now", hi: "लाइव सेशन अभी" },
  "hero.stat.rating": { en: "4.8 / 5", hi: "4.8 / 5" },
  "hero.stat.ratingLabel": { en: "Teacher Rating", hi: "शिक्षक रेटिंग" },
  "hero.stat.teachers": { en: "10,000+", hi: "10,000+" },
  "hero.stat.tutorsLabel": { en: "Expert Teachers", hi: "विशेषज्ञ शिक्षक" },
  "hero.trust.line": { en: "Trusted by 50,000+ students and parents worldwide", hi: "दुनियाभर में 50,000+ छात्रों और अभिभावकों का भरोसा" },
  "hero.trust.verified": { en: "Verified Teachers", hi: "सत्यापित शिक्षक" },
  "hero.trust.verifiedDesc": { en: "All teachers are admin-reviewed & verified", hi: "सभी शिक्षक एडमिन-समीक्षित और सत्यापित हैं" },
  "hero.trust.match": { en: "Personalised Match", hi: "व्यक्तिगत मिलान" },
  "hero.trust.matchDesc": { en: "We match you with the best teacher for your goals", hi: "हम आपके लक्ष्यों के लिए सबसे उपयुक्त शिक्षक खोजते हैं" },
  "hero.trust.flexible": { en: "Flexible Learning", hi: "लचीला सीखना" },
  "hero.trust.flexibleDesc": { en: "Your schedule, your pace — online or in-person", hi: "आपका शेड्यूल, आपकी गति — ऑनलाइन या व्यक्तिगत रूप से" },
  "hero.trust.secure": { en: "Safe & Secure", hi: "सुरक्षित" },
  "hero.trust.secureDesc": { en: "Your safety and privacy are fully protected", hi: "आपकी सुरक्षा और गोपनीयता पूरी तरह सुरक्षित है" },

  // Subjects
  "subject.mathematics": { en: "Mathematics", hi: "गणित" },
  "subject.science": { en: "Science", hi: "विज्ञान" },
  "subject.english": { en: "English", hi: "अंग्रेज़ी" },
  "subject.programming": { en: "Programming", hi: "प्रोग्रामिंग" },
  "subject.music": { en: "Music", hi: "संगीत" },
  "subject.artDesign": { en: "Art & Design", hi: "कला और डिज़ाइन" },
  "subject.languages": { en: "Languages", hi: "भाषाएँ" },
  "subject.fitness": { en: "Fitness", hi: "फिटनेस" },

  // Typing words
  "hero.word.coach": { en: "Teacher", hi: "शिक्षक" },
  "hero.word.mentor": { en: "Mentor", hi: "मेंटर" },
  "hero.word.teacher": { en: "Teacher", hi: "शिक्षक" },
  "hero.word.guide": { en: "Guide", hi: "गाइड" },
  "hero.word.expert": { en: "Expert", hi: "विशेषज्ञ" },

  // How It Works
  "how.badge": { en: "How It Works", hi: "यह कैसे काम करता है" },
  "how.headline": { en: "Find Your Teacher in", hi: "तीन चरणों में पाएं" },
  "how.clarity": { en: "Three Steps", hi: "अपना शिक्षक" },
  "how.student": { en: "I'm a Student", hi: "मैं छात्र हूँ" },
  "how.coach": { en: "I'm a Teacher", hi: "मैं शिक्षक हूँ" },
  "how.student.step1.title": { en: "Tell Us What You Need", hi: "बताएं आपको क्या चाहिए" },
  "how.student.step1.desc": { en: "Tell us your subject, level, and goals. We'll show you teachers who are the right fit — not just available.", hi: "अपना विषय, स्तर और लक्ष्य बताएं। हम आपको सिर्फ उपलब्ध नहीं, बल्कि सही शिक्षक दिखाएंगे।" },
  "how.student.step1.link": { en: "How matching works", hi: "मिलान कैसे होता है" },
  "how.student.step2.title": { en: "Browse Matched Teachers", hi: "मिले हुए शिक्षक देखें" },
  "how.student.step2.desc": { en: "Review verified profiles, read about their approach, and reach out directly. No gatekeepers.", hi: "सत्यापित प्रोफाइल देखें, उनकी शिक्षण शैली पढ़ें और सीधे संपर्क करें।" },
  "how.student.step2.link": { en: "See teacher profiles", hi: "शिक्षक प्रोफाइल देखें" },
  "how.student.step3.title": { en: "Book and Begin", hi: "बुक करें और शुरू करें" },
  "how.student.step3.desc": { en: "Pick a time, confirm your booking, and show up. Your teacher handles the rest.", hi: "समय चुनें, बुकिंग कन्फर्म करें और आ जाएं। बाकी आपका शिक्षक संभालेगा।" },
  "how.student.step3.link": { en: "What to expect in your first session", hi: "पहले सेशन में क्या होगा" },
  "how.coach.step1.title": { en: "Build Your Profile", hi: "अपना प्रोफाइल बनाएं" },
  "how.coach.step1.desc": { en: "Create your verified teacher profile — your subjects, experience, and teaching approach. Takes about 15 minutes.", hi: "अपना सत्यापित शिक्षक प्रोफाइल बनाएं — विषय, अनुभव और शिक्षण शैली। लगभग 15 मिनट लगते हैं।" },
  "how.coach.step1.link": { en: "See what goes into a profile", hi: "प्रोफाइल में क्या होता है" },
  "how.coach.step2.title": { en: "Get Admin-Approved", hi: "एडमिन से अनुमोदित हों" },
  "how.coach.step2.desc": { en: "Our team reviews your profile before it goes live. This keeps the platform trusted and your credibility visible to students.", hi: "हमारी टीम आपकी प्रोफाइल को लाइव होने से पहले समीक्षा करती है।" },
  "how.coach.step2.link": { en: "How verification works", hi: "सत्यापन कैसे काम करता है" },
  "how.coach.step3.title": { en: "Start Teaching", hi: "पढ़ाना शुरू करें" },
  "how.coach.step3.desc": { en: "Publish your offerings, accept bookings, and build your student base at your own pace.", hi: "अपनी ऑफरिंग प्रकाशित करें, बुकिंग स्वीकार करें और अपनी गति से छात्र आधार बनाएं।" },
  "how.coach.step3.link": { en: "See how offerings work", hi: "ऑफरिंग कैसे काम करती है" },

  // Social Proof — Stats Bar
  "proof.coaches": { en: "Expert Teachers", hi: "विशेषज्ञ शिक्षक" },
  "proof.students": { en: "Students Taught", hi: "पढ़ाए गए छात्र" },
  "proof.hours": { en: "Teaching Hours", hi: "शिक्षण घंटे" },
  "proof.topCoaches": { en: "Meet the Teachers", hi: "शिक्षकों से मिलें" },
  "proof.topCoaches.title": { en: "Who You'll Learn From", hi: "आप किससे सीखेंगे" },
  "proof.topCoaches.subtitle": { en: "Admin-verified teachers across a range of subjects, levels, and learning styles.", hi: "विभिन्न विषयों, स्तरों और सीखने की शैलियों में एडमिन-सत्यापित शिक्षक।" },
  "proof.groupClasses": { en: "Group Classes", hi: "ग्रुप क्लासेस" },
  "proof.groupClasses.badge": { en: "Group Classes", hi: "ग्रुप क्लासेस" },
  "proof.groupClasses.subtitle": { en: "Learn alongside others working toward the same goal — structured sessions, shared progress.", hi: "एक ही लक्ष्य की ओर काम करने वाले अन्य लोगों के साथ सीखें — संरचित सेशन, साझा प्रगति।" },
  "proof.crashCourses": { en: "Crash Courses", hi: "क्रैश कोर्सेस" },
  "proof.crashCourses.badge": { en: "Crash Courses", hi: "क्रैश कोर्सेस" },
  "proof.crashCourses.title": { en: "Prep Smarter, Not Longer", hi: "कम समय में बेहतर तैयारी" },
  "proof.crashCourses.subtitle": { en: "Short, structured programs focused on one goal — built for students who need to move fast and finish strong.", hi: "एक लक्ष्य पर केंद्रित छोटे, संरचित कार्यक्रम — उन छात्रों के लिए जिन्हें तेज़ी से आगे बढ़ना है।" },
  "proof.viewAll": { en: "View all teachers", hi: "सभी शिक्षक देखें" },
  "proof.students.label": { en: "students", hi: "छात्र" },
  "proof.testimonials": { en: "What Students Say", hi: "छात्र क्या कहते हैं" },
  "proof.testimonialsDesc": { en: "Real feedback from real learners.", hi: "वास्तविक सीखने वालों की असली प्रतिक्रिया।" },

  // Social Proof — Redefining Education
  "redefining.badge": { en: "The BMC Difference", hi: "BMC का फर्क" },
  "redefining.headline1": { en: "The Right Teacher", hi: "सही शिक्षक" },
  "redefining.headline2": { en: "Changes Everything", hi: "सब कुछ बदल देता है" },
  "redefining.subtitle": { en: "We focus on what matters — finding the perfect match between student and teacher.", hi: "हम उस पर ध्यान केंद्रित करते हैं जो मायने रखता है — छात्र और शिक्षक के बीच सही मेल खोजना।" },
  "redefining.card1.title": { en: "Admin-Reviewed Teachers", hi: "एडमिन-समीक्षित शिक्षक" },
  "redefining.card1.desc": { en: "Every teacher is manually reviewed by our team before going live. We check qualifications and teaching experience — no teacher reaches students without passing our review.", hi: "हर शिक्षक को हमारी टीम द्वारा लाइव होने से पहले मैन्युअली समीक्षित किया जाता है। हम योग्यता और शिक्षण अनुभव की जाँच करते हैं।" },
  "redefining.card1.verified": { en: "Verified teachers", hi: "सत्यापित शिक्षक" },
  "redefining.card1.satisfaction": { en: "Satisfaction rate", hi: "संतुष्टि दर" },
  "redefining.card1.rating": { en: "Average rating", hi: "औसत रेटिंग" },
  "redefining.card2.title": { en: "Matched to You, Not Just the Topic", hi: "सिर्फ विषय नहीं, आपके लिए सही मेल" },
  "redefining.card2.desc": { en: "Tell us your subject, level, and goals. Browse verified profiles and connect directly with teachers who suit your learning style — not just the topic.", hi: "अपना विषय, स्तर और लक्ष्य बताएं। सत्यापित प्रोफाइल देखें और सीधे उन शिक्षकों से जुड़ें जो आपकी सीखने की शैली के अनुसार हों।" },
  "redefining.card2.accuracy": { en: "match accuracy", hi: "मिलान सटीकता" },
  "redefining.card3.title": { en: "Flexible Scheduling", hi: "लचीला शेड्यूलिंग" },
  "redefining.card3.desc": { en: "Book sessions around your schedule. Choose 1-on-1 or group, pick any available slot, and reschedule up to 24 hours in advance with no penalty.", hi: "अपने शेड्यूल के अनुसार सेशन बुक करें। 1-on-1 या ग्रुप चुनें, कोई भी उपलब्ध स्लॉट चुनें, और 24 घंटे पहले बिना किसी जुर्माने के रीशेड्यूल करें।" },
  "redefining.card4.title": { en: "Learn Without the Guesswork", hi: "बिना अनिश्चितता के सीखें" },
  "redefining.card4.desc": { en: "Read verified teacher profiles, review their teaching approach, and reach out before you book. Know who you're learning from before you commit.", hi: "सत्यापित शिक्षक प्रोफाइल पढ़ें, उनके शिक्षण दृष्टिकोण की समीक्षा करें, और बुक करने से पहले संपर्क करें। प्रतिबद्ध होने से पहले जानें कि आप किससे सीख रहे हैं।" },
  "redefining.card5.heading": { en: "Ready to start?", hi: "शुरू करने के लिए तैयार?" },
  "redefining.card5.sub": { en: "Find your perfect match today.", hi: "आज अपना परफेक्ट मैच खोजें।" },
  "redefining.card5.cta": { en: "Browse Teachers", hi: "शिक्षक देखें" },

  // Social Proof — Voices of Our Community
  "voices.badge": { en: "Real Stories", hi: "वास्तविक कहानियाँ" },
  "voices.headline1": { en: "Voices of Our", hi: "हमारे समुदाय की" },
  "voices.headline2": { en: "Community", hi: "आवाज़ें" },
  "voices.subtitle": { en: "From students reaching their goals to teachers growing their practice — hear it from those who live it.", hi: "लक्ष्य हासिल करते छात्रों से लेकर अपना करियर बढ़ाते शिक्षकों तक — उन्हीं से सुनें जो इसे जीते हैं।" },
  "voices.tab.students": { en: "Student Stories", hi: "छात्रों की कहानियाँ" },
  "voices.tab.coaches": { en: "Teacher Experiences", hi: "शिक्षक के अनुभव" },
  "voices.verifiedCoach": { en: "Verified Teacher", hi: "सत्यापित शिक्षक" },
  "voices.stat.satisfaction": { en: "Student Satisfaction", hi: "छात्र संतुष्टि" },
  "voices.stat.rating": { en: "Average Teacher Rating", hi: "औसत शिक्षक रेटिंग" },
  "voices.stat.stories": { en: "Success Stories", hi: "सफलता की कहानियाँ" },

  // CTA Section
  "cta.badge.student": { en: "Start for free", hi: "मुफ़्त में शुरू करें" },
  "cta.badge.coach": { en: "No platform fees to start", hi: "शुरुआत में कोई प्लेटफॉर्म शुल्क नहीं" },
  "cta.headline.student": { en: "Ready to Start Learning?", hi: "सीखना शुरू करने के लिए तैयार हैं?" },
  "cta.headline.coach": { en: "Ready to Start Teaching?", hi: "पढ़ाना शुरू करने के लिए तैयार हैं?" },
  "cta.sub.student": { en: "Join thousands of students who found their perfect teacher match.", hi: "हजारों छात्रों से जुड़ें जिन्होंने अपना परफेक्ट शिक्षक पाया।" },
  "cta.sub.coach": { en: "Join our community of educators and start earning today.", hi: "हमारे शिक्षकों के समुदाय से जुड़ें और आज ही कमाई शुरू करें।" },
  "cta.btn": { en: "Get Started for Free", hi: "मुफ़्त में शुरू करें" },
  "cta.trust": { en: "No credit card required · Free forever for basic features", hi: "क्रेडिट कार्ड की ज़रूरत नहीं · बेसिक फीचर्स हमेशा मुफ़्त" },

  // Common
  "common.home": { en: "Home", hi: "होम" },
  "common.search": { en: "Search", hi: "खोजें" },
};

interface LanguageContextType {
  language: LangCode;
  setLanguage: (lang: LangCode) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<LangCode>("en");

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => {
      const entry = translations[key];
      let text = entry?.[language] ?? entry?.en ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, v);
        });
      }
      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
